import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Key, Eye, EyeOff } from 'lucide-react';
import { validatePassword, checkRateLimit, logSecurityEvent } from '@/lib/validation';
import { useAuth } from '@/hooks/useAuth';

const passwordSchema = z.object({
  currentPassword: z.string().min(1, 'Current password is required'),
  newPassword: z.string().refine((val) => {
    const result = validatePassword(val);
    return result.isValid;
  }, {
    message: 'Password must be at least 6 characters with uppercase and lowercase letters'
  }),
  confirmPassword: z.string().min(1, 'Please confirm your password')
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"]
}).refine((data) => data.currentPassword !== data.newPassword, {
  message: "New password must be different from current password",
  path: ["newPassword"]
});

type PasswordFormData = z.infer<typeof passwordSchema>;

export const ChangePassword = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState<'weak' | 'fair' | 'strong' | null>(null);

  const { register, handleSubmit, formState: { errors }, watch, reset } = useForm<PasswordFormData>({
    resolver: zodResolver(passwordSchema)
  });

  const newPassword = watch('newPassword');

  // Calculate password strength
  const calculateStrength = (password: string): 'weak' | 'fair' | 'strong' => {
    if (!password) return 'weak';
    
    let score = 0;
    if (password.length >= 8) score++;
    if (password.length >= 12) score++;
    if (/[A-Z]/.test(password) && /[a-z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;
    
    if (score <= 2) return 'weak';
    if (score <= 4) return 'fair';
    return 'strong';
  };

  // Update strength indicator when password changes
  useState(() => {
    if (newPassword) {
      setPasswordStrength(calculateStrength(newPassword));
    } else {
      setPasswordStrength(null);
    }
  });

  const onSubmit = async (data: PasswordFormData) => {
    // Rate limiting check
    const rateLimitKey = `change-password-${user?.id}`;
    const rateLimit = checkRateLimit(rateLimitKey, 3, 300000); // 3 attempts per 5 minutes
    
    if (!rateLimit.allowed) {
      toast.error('Too many attempts. Please try again in 5 minutes.');
      logSecurityEvent('password_change_rate_limit', user?.id);
      return;
    }

    try {
      setLoading(true);

      // Step 1: Verify current password by attempting to sign in
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user?.email || '',
        password: data.currentPassword,
      });

      if (signInError) {
        toast.error('Current password is incorrect');
        logSecurityEvent('password_change_failed_verification', user?.id);
        return;
      }

      // Step 2: Update to new password
      const { error: updateError } = await supabase.auth.updateUser({
        password: data.newPassword
      });

      if (updateError) {
        throw updateError;
      }

      // Success
      reset();
      setPasswordStrength(null);
      toast.success('Password updated successfully!');
      logSecurityEvent('password_changed', user?.id, { success: true });

    } catch (error: any) {
      console.error('Password change error:', error);
      toast.error(error.message || 'Failed to update password. Please try again.');
      logSecurityEvent('password_change_error', user?.id, { error: error.message });
    } finally {
      setLoading(false);
    }
  };

  const getStrengthColor = (strength: 'weak' | 'fair' | 'strong' | null) => {
    if (!strength) return 'bg-muted';
    if (strength === 'weak') return 'bg-red-500';
    if (strength === 'fair') return 'bg-amber-500';
    return 'bg-green-500';
  };

  const getStrengthLabel = (strength: 'weak' | 'fair' | 'strong' | null) => {
    if (!strength) return '';
    if (strength === 'weak') return 'Weak';
    if (strength === 'fair') return 'Fair';
    return 'Strong';
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Key className="h-5 w-5" />
          Change Password
        </CardTitle>
        <CardDescription>
          Update your password to keep your account secure
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="currentPassword">Current Password</Label>
            <div className="relative">
              <Input
                id="currentPassword"
                type={showCurrent ? 'text' : 'password'}
                {...register('currentPassword')}
                placeholder="Enter your current password"
                disabled={loading}
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                onClick={() => setShowCurrent(!showCurrent)}
                tabIndex={-1}
              >
                {showCurrent ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
            </div>
            {errors.currentPassword && (
              <p className="text-sm text-destructive">{errors.currentPassword.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="newPassword">New Password</Label>
            <div className="relative">
              <Input
                id="newPassword"
                type={showNew ? 'text' : 'password'}
                {...register('newPassword')}
                placeholder="Enter your new password"
                disabled={loading}
                onChange={(e) => {
                  register('newPassword').onChange(e);
                  setPasswordStrength(calculateStrength(e.target.value));
                }}
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                onClick={() => setShowNew(!showNew)}
                tabIndex={-1}
              >
                {showNew ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
            </div>
            {passwordStrength && (
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                    <div 
                      className={`h-full transition-all ${getStrengthColor(passwordStrength)}`}
                      style={{ width: passwordStrength === 'weak' ? '33%' : passwordStrength === 'fair' ? '66%' : '100%' }}
                    />
                  </div>
                  <span className="text-xs font-medium">{getStrengthLabel(passwordStrength)}</span>
                </div>
              </div>
            )}
            {errors.newPassword && (
              <p className="text-sm text-destructive">{errors.newPassword.message}</p>
            )}
            <p className="text-xs text-muted-foreground">
              At least 6 characters with uppercase and lowercase letters
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmPassword">Confirm New Password</Label>
            <div className="relative">
              <Input
                id="confirmPassword"
                type={showConfirm ? 'text' : 'password'}
                {...register('confirmPassword')}
                placeholder="Confirm your new password"
                disabled={loading}
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                onClick={() => setShowConfirm(!showConfirm)}
                tabIndex={-1}
              >
                {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
            </div>
            {errors.confirmPassword && (
              <p className="text-sm text-destructive">{errors.confirmPassword.message}</p>
            )}
          </div>

          <Button type="submit" disabled={loading} className="w-full">
            {loading ? 'Updating Password...' : 'Update Password'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};
